// Command Center dashboard (Phase 11.1 + 11.2 + 11.3).
//
// Layout:
//   - Greeting (top)
//   - Pulse section (Phase 11.2): 4 cross-cutting KPI cards in a row
//   - 2-column body: What's New feed (Phase 11.3) | Applications grid (Phase 11.1)
//
// Admin authoring of posts is inline at the top of the feed column —
// pattern matches CategoriesAdminPage's "Add root category" inline form.
// Non-admin users see only the read-only feed.
//
// Pattern follows the Workflow surfaces — TanStack Query for fetch,
// useMutation for write, brand-locked palette, no React Hook Form.
// Dark variant of the brand theme: Chocolate Brown background, Cream
// text, Peach accents.

import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { Plus, Trash2, X } from "lucide-react";
import { ApiError, api } from "@/lib/api";
import { useAuth } from "@/auth/AuthProvider";
import { cn } from "@/lib/utils";

type AppSection = "apps" | "backend";

interface CommandCenterApp {
  slug: string;
  name: string;
  monogram: string;
  description: string;
  href: string;
  status: "live" | "soon" | "coming_soon";
  badgeTone: "peach" | "rust" | "cream" | "brown";
  section: AppSection;
}

interface AppsResponse {
  apps: CommandCenterApp[];
}

const BADGE_BG: Record<CommandCenterApp["badgeTone"], string> = {
  peach: "bg-peach-400 text-white",
  rust: "bg-ink text-ink",
  cream: "bg-cream-200 text-brown-500",
  brown: "bg-brown-300 text-ink",
};

const STATUS_LABEL: Record<CommandCenterApp["status"], string> = {
  live: "LIVE",
  soon: "SOON",
  coming_soon: "COMING SOON",
};

const STATUS_CHIP: Record<CommandCenterApp["status"], string> = {
  live: "bg-peach-400 text-white",
  soon: "border border-cream-200/40 text-muted",
  coming_soon: "border border-risk/40 text-risk",
};

function timeOfDayGreeting(): string {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 18) return "Good afternoon";
  return "Good evening";
}

function firstName(fullName: string | null | undefined, email: string): string {
  if (fullName && fullName.trim().length > 0) {
    return fullName.split(/\s+/)[0] ?? "";
  }
  return email.split("@")[0] ?? "there";
}

type BadgeKind = "announcement" | "update" | "new_release" | "note";
type MonogramTone = "peach" | "rust" | "cream" | "brown";

interface CommandPost {
  id: string;
  title: string;
  body: string;
  badgeKind: BadgeKind;
  monogramTone: MonogramTone;
  pinned: boolean;
  publishedAt: string;
  authorMonogram: string;
  authorName: string | null;
  authorId: string;
  createdAt: string | null;
  updatedAt: string | null;
}

interface PostsResponse {
  rows: CommandPost[];
  nextCursor: { publishedAt: string; id: string } | null;
}

// Phase 11.2 Daily Dash KPI shape.
type PulseSlug =
  | "active-orders"
  | "in-production"
  | "open-exceptions"
  | "active-clients";

interface PulseKpi {
  slug: PulseSlug;
  label: string;
  value: number;
  href: string;
  description: string;
}

// Phase 11.5 Backlog KPI shape — triage queues (things needing action),
// rendered as a separate section from Pulse (things currently flowing).
type BacklogSlug = "unreviewed-signups" | "pending-project-requests";

interface BacklogKpi {
  slug: BacklogSlug;
  label: string;
  value: number;
  href: string;
  description: string;
}

interface DailyDashResponse {
  pulse: PulseKpi[];
  backlog: BacklogKpi[];
  fetchedAt: string;
}

const BADGE_KINDS: Array<{ value: BadgeKind; label: string; tone: string }> = [
  {
    value: "announcement",
    label: "ANNOUNCEMENT",
    tone: "border-ink text-ink",
  },
  { value: "update", label: "UPDATE", tone: "border-cream-200 text-ink" },
  {
    value: "new_release",
    label: "NEW RELEASE",
    tone: "border-risk/40 text-risk",
  },
  { value: "note", label: "NOTE", tone: "border-brown-300 text-muted" },
];

const MONOGRAM_BG: Record<MonogramTone, string> = {
  peach: "bg-peach-400 text-white",
  rust: "bg-ink text-ink",
  cream: "bg-cream-200 text-brown-500",
  brown: "bg-brown-300 text-ink",
};

export function CommandCenterDashboardPage() {
  const { user } = useAuth();
  const isAdmin = user?.roles.includes("admin") ?? false;
  const queryClient = useQueryClient();

  const appsKey = useMemo(() => ["command", "apps"] as const, []);
  const postsKey = useMemo(() => ["command", "posts"] as const, []);
  const pulseKey = useMemo(() => ["command", "dashboard"] as const, []);

  const appsQ = useQuery({
    queryKey: appsKey,
    queryFn: () => api<AppsResponse>("/command/apps"),
    enabled: user !== null,
    staleTime: 60_000,
  });

  const postsQ = useQuery({
    queryKey: postsKey,
    queryFn: () => api<PostsResponse>("/command/posts?limit=20"),
    enabled: user !== null,
    staleTime: 60_000,
  });

  // Daily Dash pulse — auto-refreshes every 60s so the home page is a
  // genuine real-time view, matching the Production Dashboard cadence.
  const pulseQ = useQuery({
    queryKey: pulseKey,
    queryFn: () => api<DailyDashResponse>("/command/dashboard"),
    enabled: user !== null,
    refetchInterval: 60_000,
    refetchIntervalInBackground: false,
    staleTime: 30_000,
  });

  const greeting = useMemo(() => timeOfDayGreeting(), []);
  const name = user ? firstName(user.name, user.email) : "there";

  const apps = appsQ.data?.apps ?? [];
  const posts = postsQ.data?.rows ?? [];

  return (
    <div className="space-y-12">
      {/* Greeting */}
      <div>
        <h1 className="text-[32px] font-semibold leading-[1.15] tracking-[-1px] text-ink">
          {greeting}, {name}
        </h1>
        <p className="mt-3 text-base text-muted">
          Your tools and the latest from the team.
        </p>
      </div>

      {/* Pulse — Daily Dash KPIs (Phase 11.2) */}
      <PulseSection
        pulse={pulseQ.data?.pulse ?? []}
        loading={pulseQ.isLoading}
        error={pulseQ.isError}
        fetchedAt={pulseQ.data?.fetchedAt ?? null}
        isFetching={pulseQ.isFetching}
      />

      {/* Backlog — triage queues (Phase 11.5). Rendered only when there
          is anything to render (or while still loading the first time) —
          a "zero backlog" state would just be empty visual noise on a
          dashboard otherwise full of running work. */}
      <BacklogSection
        backlog={pulseQ.data?.backlog ?? []}
        loading={pulseQ.isLoading}
      />

      {/* Application sections — Apps (customer-facing) + Backend / Edit
          (admin curation), then the What's New feed underneath. */}
      <ApplicationsSection
        apps={apps}
        loading={appsQ.isLoading}
        error={appsQ.isError}
      />

      {/* What's New feed */}
      <WhatsNewSection
        posts={posts}
        loading={postsQ.isLoading}
        error={postsQ.isError}
        isAdmin={isAdmin}
        onMutated={() => {
          void queryClient.invalidateQueries({ queryKey: postsKey });
        }}
      />
    </div>
  );
}

// ─── Applications (two sections: Apps + Backend) ──────────────────────────

function ApplicationsSection(props: {
  apps: CommandCenterApp[];
  loading: boolean;
  error: boolean;
}) {
  const { apps, loading, error } = props;
  const customerApps = apps.filter((a) => a.section === "apps");
  const backendApps = apps.filter((a) => a.section === "backend");

  return (
    <div className="space-y-10">
      <AppsSubsection
        eyebrow="Apps"
        helper="Customer-facing surfaces + the main staff workspace."
        apps={customerApps}
        loading={loading}
        error={error}
        emptyLabel="No apps visible — toggle access in Admin · Access."
      />
      {backendApps.length > 0 && (
        <AppsSubsection
          eyebrow="Backend / Edit"
          helper="Admin curation tools that shape what the customer sees."
          apps={backendApps}
          loading={false}
          error={false}
          emptyLabel="No admin tiles visible."
        />
      )}
    </div>
  );
}

function AppsSubsection(props: {
  eyebrow: string;
  helper: string;
  apps: CommandCenterApp[];
  loading: boolean;
  error: boolean;
  emptyLabel: string;
}) {
  const { eyebrow, helper, apps, loading, error, emptyLabel } = props;
  return (
    <section>
      <div className="mb-4 flex items-baseline justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted">
            {eyebrow}
          </p>
          <p className="mt-1 text-xs text-muted">{helper}</p>
        </div>
      </div>
      {loading && (
        <p className="text-sm text-muted">Loading…</p>
      )}
      {error && (
        <p className="text-sm text-risk">
          Could not load the applications grid.
        </p>
      )}
      {!loading && !error && apps.length === 0 && (
        <p className="text-sm text-muted">{emptyLabel}</p>
      )}
      {!loading && !error && apps.length > 0 && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {apps.map((app) => (
            <AppTile key={app.slug} app={app} />
          ))}
        </div>
      )}
    </section>
  );
}

// ─── What's New (feed + admin authoring) ─────────────────────────────────

function WhatsNewSection(props: {
  posts: CommandPost[];
  loading: boolean;
  error: boolean;
  isAdmin: boolean;
  onMutated: () => void;
}) {
  const { posts, loading, error, isAdmin, onMutated } = props;
  const [composing, setComposing] = useState(false);

  return (
    <section>
      <div className="mb-4 flex items-center justify-between">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted">
          What's New
        </p>
        {isAdmin && !composing && (
          <button
            type="button"
            onClick={() => setComposing(true)}
            className="inline-flex items-center gap-1 rounded-md border border-rule px-2.5 py-1 text-xs font-semibold text-ink hover:bg-white"
          >
            <Plus className="h-3.5 w-3.5" /> New post
          </button>
        )}
      </div>

      {composing && (
        <div className="mb-4">
          <PostComposer
            onCancel={() => setComposing(false)}
            onSaved={() => {
              setComposing(false);
              onMutated();
            }}
          />
        </div>
      )}

      {loading && <p className="text-sm text-muted">Loading…</p>}
      {error && (
        <p className="text-sm text-risk">Could not load the feed.</p>
      )}
      {!loading && !error && posts.length === 0 && !composing && (
        <div className="rounded-lg border border-rule bg-white px-5 py-6">
          <p className="text-sm text-muted">
            No posts yet. Release notes, brand updates, announcements show up
            here as the team adds them.
          </p>
        </div>
      )}

      {posts.length > 0 && (
        <ol className="space-y-3">
          {posts.map((post) => (
            <PostCard
              key={post.id}
              post={post}
              isAdmin={isAdmin}
              onMutated={onMutated}
            />
          ))}
        </ol>
      )}
    </section>
  );
}

function PostCard(props: {
  post: CommandPost;
  isAdmin: boolean;
  onMutated: () => void;
}) {
  const { post, isAdmin, onMutated } = props;
  const [error, setError] = useState<string | null>(null);
  const badge = BADGE_KINDS.find((b) => b.value === post.badgeKind);

  const remove = useMutation({
    mutationFn: async () =>
      api(`/command/posts/${post.id}`, { method: "DELETE" }),
    onError: (err) => setError(extractError(err)),
    onSuccess: () => {
      setError(null);
      onMutated();
    },
  });

  return (
    <li className="rounded-lg border border-rule bg-white px-4 py-4">
      <div className="flex items-start gap-3">
        <span
          className={cn(
            "flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md font-display text-sm font-semibold",
            MONOGRAM_BG[post.monogramTone],
          )}
          aria-hidden
        >
          {post.authorMonogram}
        </span>
        <div className="min-w-0 flex-1">
          <h3 className="font-semibold text-ink">{post.title}</h3>
          <p className="mt-1 whitespace-pre-line text-sm text-muted">
            {post.body}
          </p>
          <div className="mt-3 flex items-center justify-between gap-2">
            {badge && (
              <span
                className={cn(
                  "inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide",
                  badge.tone,
                )}
              >
                {badge.label}
              </span>
            )}
            <span className="text-[11px] text-muted">
              {timeAgo(post.publishedAt)}
            </span>
          </div>
          {error && <p className="mt-2 text-xs text-risk">{error}</p>}
          {isAdmin && (
            <div className="mt-2 flex justify-end">
              <button
                type="button"
                onClick={() => remove.mutate()}
                disabled={remove.isPending}
                className="inline-flex items-center gap-1 text-[11px] font-semibold text-muted hover:text-risk"
              >
                <Trash2 className="h-3 w-3" />{" "}
                {remove.isPending ? "Removing…" : "Delete"}
              </button>
            </div>
          )}
        </div>
      </div>
    </li>
  );
}

function PostComposer(props: { onCancel: () => void; onSaved: () => void }) {
  const { onCancel, onSaved } = props;
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [badgeKind, setBadgeKind] = useState<BadgeKind>("note");
  const [monogramTone, setMonogramTone] = useState<MonogramTone>("peach");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") onCancel();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onCancel]);

  const create = useMutation({
    mutationFn: async () =>
      api<{ post: CommandPost }>("/command/posts", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          title: title.trim(),
          body: body.trim(),
          badge_kind: badgeKind,
          monogram_tone: monogramTone,
        }),
      }),
    onError: (err) => setError(extractError(err)),
    onSuccess: () => {
      setError(null);
      onSaved();
    },
  });

  const submit = (e: React.FormEvent): void => {
    e.preventDefault();
    setError(null);
    if (title.trim().length === 0) {
      setError("Title is required.");
      return;
    }
    if (body.trim().length === 0) {
      setError("Body is required.");
      return;
    }
    create.mutate();
  };

  return (
    <form
      onSubmit={submit}
      className="rounded-lg border border-peach-300/70 bg-brown-500/40 p-4"
    >
      <div className="mb-3 flex items-center justify-between gap-3">
        <p className="text-sm font-semibold text-ink">New post</p>
        <button
          type="button"
          onClick={onCancel}
          className="text-muted hover:text-ink"
          aria-label="Cancel"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="space-y-3">
        <input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Title"
          maxLength={140}
          autoFocus
          className="w-full rounded-md border border-rule bg-white px-3 py-2 text-sm font-semibold text-ink placeholder:text-muted focus:border-peach-400 focus:outline-none focus:ring-2 focus:ring-peach-400/40"
        />
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="Body — 1–3 sentences. Plain text."
          rows={4}
          maxLength={2000}
          className="w-full rounded-md border border-rule bg-white px-3 py-2 text-sm text-ink placeholder:text-muted focus:border-peach-400 focus:outline-none focus:ring-2 focus:ring-peach-400/40"
        />
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="block">
            <span className="mb-1 block text-[10px] font-semibold uppercase tracking-[0.2em] text-muted">
              Badge
            </span>
            <select
              value={badgeKind}
              onChange={(e) => setBadgeKind(e.target.value as BadgeKind)}
              className="w-full rounded-md border border-rule bg-white px-3 py-2 text-sm text-ink focus:border-peach-400 focus:outline-none focus:ring-2 focus:ring-peach-400/40"
            >
              {BADGE_KINDS.map((b) => (
                <option key={b.value} value={b.value}>
                  {b.label}
                </option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="mb-1 block text-[10px] font-semibold uppercase tracking-[0.2em] text-muted">
              Monogram color
            </span>
            <select
              value={monogramTone}
              onChange={(e) =>
                setMonogramTone(e.target.value as MonogramTone)
              }
              className="w-full rounded-md border border-rule bg-white px-3 py-2 text-sm text-ink focus:border-peach-400 focus:outline-none focus:ring-2 focus:ring-peach-400/40"
            >
              <option value="peach">Peach</option>
              <option value="rust">Rust</option>
              <option value="cream">Cream</option>
              <option value="brown">Brown</option>
            </select>
          </label>
        </div>
      </div>

      {error && <p className="mt-3 text-sm text-risk">{error}</p>}

      <div className="mt-4 flex justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          disabled={create.isPending}
          className="rounded-md px-4 py-2 text-sm font-semibold text-ink hover:bg-white"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={create.isPending}
          className="rounded-md bg-peach-400 px-4 py-2 text-sm font-semibold text-white hover:bg-peach-500 disabled:opacity-60"
        >
          {create.isPending ? "Posting…" : "Post"}
        </button>
      </div>
    </form>
  );
}

// ─── helpers ──────────────────────────────────────────────────────────────

function timeAgo(iso: string): string {
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diffSec = Math.max(0, Math.floor((now - then) / 1000));
  if (diffSec < 60) return "just now";
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHour = Math.floor(diffMin / 60);
  if (diffHour < 24) return `${diffHour}h ago`;
  const diffDay = Math.floor(diffHour / 24);
  if (diffDay < 7) return `${diffDay}d ago`;
  const diffWeek = Math.floor(diffDay / 7);
  if (diffWeek < 5) return `${diffWeek}w ago`;
  const diffMonth = Math.floor(diffDay / 30);
  if (diffMonth < 12) return `${diffMonth}mo ago`;
  const diffYear = Math.floor(diffDay / 365);
  return `${diffYear}y ago`;
}

function extractError(err: unknown): string {
  if (err instanceof ApiError) {
    const body = err.body as { error?: string; message?: string } | null;
    return body?.message ?? body?.error ?? `Request failed (${err.status}).`;
  }
  return "Request failed. Try again or check your connection.";
}

function AppTile({ app }: { app: CommandCenterApp }) {
  const isLive = app.status === "live";
  const tile = (
    <article
      className={cn(
        "relative h-full rounded-lg border border-rule bg-white p-5 transition-colors",
        isLive
          ? "hover:border-ink hover:bg-canvas"
          : "opacity-70",
      )}
    >
      {/* Status chip top-right */}
      <span
        className={cn(
          "absolute right-4 top-4 rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide",
          STATUS_CHIP[app.status],
        )}
      >
        {STATUS_LABEL[app.status]}
      </span>

      {/* Monogram badge */}
      <div
        className={cn(
          "flex h-10 w-10 items-center justify-center rounded-md font-display text-lg font-semibold",
          BADGE_BG[app.badgeTone],
        )}
        aria-hidden
      >
        {app.monogram}
      </div>

      <h3 className="mt-5 font-display text-lg font-bold text-ink">
        {app.name}
      </h3>
      <p className="mt-2 text-sm leading-relaxed text-muted">
        {app.description}
      </p>
    </article>
  );

  if (isLive && app.href !== "#") {
    return (
      <Link to={app.href} className="block focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-peach-400 focus-visible:ring-offset-2 focus-visible:ring-offset-brown-700 rounded-lg">
        {tile}
      </Link>
    );
  }
  return tile;
}

// ─── Daily Dash pulse (Phase 11.2) ─────────────────────────────────────────

function PulseSection(props: {
  pulse: PulseKpi[];
  loading: boolean;
  error: boolean;
  fetchedAt: string | null;
  isFetching: boolean;
}) {
  const { pulse, loading, error, fetchedAt, isFetching } = props;
  return (
    <section>
      <div className="mb-4 flex items-baseline justify-between gap-3">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted">
          Pulse
        </p>
        {fetchedAt && (
          <p className="text-[11px] text-muted">
            {isFetching ? "Refreshing…" : `As of ${formatTime(fetchedAt)}`}
          </p>
        )}
      </div>
      {loading && pulse.length === 0 && (
        <p className="text-sm text-muted">Loading pulse…</p>
      )}
      {error && (
        <p className="text-sm text-risk">
          Could not load the pulse. Refreshing in a minute.
        </p>
      )}
      {pulse.length > 0 && (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {pulse.map((kpi) => (
            <PulseCard key={kpi.slug} kpi={kpi} />
          ))}
        </div>
      )}
    </section>
  );
}

function PulseCard({ kpi }: { kpi: PulseKpi }) {
  const card = (
    <article className="h-full rounded-lg border border-rule bg-white p-5 transition-colors hover:border-ink hover:bg-canvas">
      <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted">
        {kpi.label}
      </p>
      <p className="mt-2 font-display text-4xl font-semibold tracking-tight text-ink">
        {kpi.value.toLocaleString()}
      </p>
      <p className="mt-3 text-xs leading-relaxed text-muted">
        {kpi.description}
      </p>
    </article>
  );
  if (kpi.href !== "#") {
    return (
      <Link
        to={kpi.href}
        className="block rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-peach-400 focus-visible:ring-offset-2 focus-visible:ring-offset-brown-700"
      >
        {card}
      </Link>
    );
  }
  return card;
}

function formatTime(iso: string): string {
  const d = new Date(iso);
  const hh = d.getHours();
  const mm = d.getMinutes().toString().padStart(2, "0");
  const ampm = hh >= 12 ? "pm" : "am";
  const h12 = ((hh + 11) % 12) + 1;
  return `${h12}:${mm} ${ampm}`;
}

// ─── Backlog (Phase 11.5) ──────────────────────────────────────────────────

function BacklogSection(props: {
  backlog: BacklogKpi[];
  loading: boolean;
}) {
  const { backlog, loading } = props;
  // Hide the section entirely if every counter is zero — keeps the home
  // page calm when there's nothing to triage. Show during the loading
  // window so the layout doesn't jump.
  const totalPending = backlog.reduce((sum, b) => sum + b.value, 0);
  if (!loading && totalPending === 0) return null;
  return (
    <section>
      <div className="mb-4 flex items-baseline justify-between gap-3">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted">
          Needs your review
        </p>
        {totalPending > 0 && (
          <p className="text-[11px] text-muted">
            {totalPending} item{totalPending === 1 ? "" : "s"} waiting
          </p>
        )}
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        {backlog.map((kpi) => (
          <BacklogCard key={kpi.slug} kpi={kpi} />
        ))}
      </div>
    </section>
  );
}

function BacklogCard({ kpi }: { kpi: BacklogKpi }) {
  const hasWork = kpi.value > 0;
  const card = (
    <article
      className={cn(
        "h-full rounded-lg border p-5 transition-colors",
        hasWork
          ? "border-risk/40 bg-rust-500/20 hover:border-risk/40 hover:bg-rust-500/30"
          : "border-rule bg-white hover:border-ink hover:bg-canvas",
      )}
    >
      <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted">
        {kpi.label}
      </p>
      <p
        className={cn(
          "mt-2 font-display text-4xl font-semibold tracking-tight",
          hasWork ? "text-ink" : "text-muted",
        )}
      >
        {kpi.value.toLocaleString()}
      </p>
      <p className="mt-3 text-xs leading-relaxed text-muted">
        {kpi.description}
      </p>
    </article>
  );
  if (kpi.href !== "#") {
    return (
      <Link
        to={kpi.href}
        className="block rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-peach-400 focus-visible:ring-offset-2 focus-visible:ring-offset-brown-700"
      >
        {card}
      </Link>
    );
  }
  return card;
}
